import * as React from 'react';
import { History } from 'history';
import * as UrlParams from 'ts/constants/UrlParams';
import { USERNAME } from 'ts/constants/UrlParams';
import { errorForm, ITaskData, navigation, task } from '@origin-digital/reporting-client';
import { validateUsername } from './UsernameValidator';
import { sendOneTimeCodeRequest } from '../../../services/registration/RegistrationService';
import PageConfig from '../../../../services/userflow/PageConfig';
import { EnterUsernameFormView } from './sub-components/EnterUsernameFormView';
import * as Window from 'ts/window/Window';

interface EnterUsernameFormProps {
  history: History;
  location: Location;
  nextAction: (urlParams: string) => void;
  navigateToErrorPage: () => void;
  customErrorAction?: (username: string) => void;
  config: PageConfig;
  infoText: string;
}

interface EnterUsernamePageState {
  username: string;
  usernameFieldTouched: boolean;
  error: boolean;
  spinner: boolean;
  inlineErrorMessage: string;
}

class EnterUsernameForm extends React.PureComponent<
  EnterUsernameFormProps,
  EnterUsernamePageState
> {
  public state = {
    username: '',
    usernameFieldTouched: false,
    error: false,
    spinner: false,
    inlineErrorMessage: '',
  };

  public componentDidMount() {
    const usernameHashParam = new URLSearchParams(this.props.location.search).get(USERNAME);
    const usernameUrlParam = new URLSearchParams(Window.getWindow().location.search).get(USERNAME);
    if (usernameUrlParam) {
      this.setState({ username: usernameUrlParam });
    } else if (usernameHashParam) {
      // Not sure why but the param comes in the URL querystring for native, but hash params for web.
      this.setState({ username: usernameHashParam });
    }
    navigation('screen', {
      currentUri: '',
      friendlyUri: `originenergy:my-account:auth:${this.props.config.analytics.pageName}`,
    });
  }

  public render() {
    return (
      <EnterUsernameFormView
        username={this.state.username}
        onUsernameBlur={this.usernameBlurred}
        onUsernameChange={this.usernameChanged}
        onSubmit={this.submit}
        error={this.state.error}
        spinner={this.state.spinner}
        fieldNormalizer={removeSpacesNormalizer}
        inlineErrorMessage={this.state.inlineErrorMessage}
        infoText={this.props.infoText}
      />
    );
  }

  private submit = async (e) => {
    e.preventDefault();
    const { username } = this.state;

    const usernameError = validateUsername(username);
    if (usernameError) {
      errorForm({
        formName: this.props.config.analytics.taskName,
        errorMessage: 'email-invalid-format',
        errorCode: 'email-invalid-format',
      });
      this.setState({ inlineErrorMessage: usernameError });
      return;
    }
    this.setState({ spinner: true });

    try {
      if (await sendOneTimeCodeRequest(username)) {
        this.navigateToOneTimeCode();
      } else {
        const isEmail = /@/.test(username);
        errorForm({
          formName: this.props.config.analytics.taskName,
          errorMessage: isEmail ? 'email-does-not-exist' : 'number-does-not-exist',
          errorCode: isEmail ? 'email-does-not-exist' : 'number-does-not-exist',
        });
        this.setError();
      }
    } catch (error) {
      this.props.navigateToErrorPage();
    }
  };

  private setError() {
    if (this.props.customErrorAction) {
      this.props.customErrorAction(this.state.username);
    } else {
      this.setState({ spinner: false, error: true });
    }
  }

  private usernameBlurred = (e) => {
    const username = e.target.value;
    const usernameFormatError = validateUsername(username);
    this.setState({
      inlineErrorMessage: usernameFormatError,
    });
  };

  private usernameChanged = (e) => {
    const username = e.target.value;
    if (!this.state.usernameFieldTouched) {
      task.base(this.props.config.analytics.taskName as any, { action: 'start' } as ITaskData);
    }

    this.setState({
      username,
      usernameFieldTouched: true,
      error: false,
      inlineErrorMessage: validateUsername(username),
    });
  };

  private navigateToOneTimeCode = () => {
    const urlParams = new URLSearchParams({
      [UrlParams.EMAIL_OR_PHONE]: this.state.username,
    } as any).toString();
    this.props.nextAction(urlParams);
  };
}

const removeSpacesNormalizer = (value, previousValue?): string => {
  return value.replace(/\s+/g, '');
};

export { EnterUsernameForm, removeSpacesNormalizer };
